import React, { useEffect, useState } from 'react';

import { LinkOutlined } from '@ant-design/icons';

import { List, Divider, Skeleton, message, Typography, Alert, Button, Card } from 'antd';
import UserStore from 'stores/UserStore';
import AddEditNDAForm from 'components/NDAForm/AddEdit';
import RemoveConfirm from 'components/shared/RemoveConfirm';
import { IFile, INDAFormPayload } from 'types/types';
import { DownloadFile, get } from '@partsbadger/utils';

import UIStore from '@/stores/UIStore';
import Verify from '../../../components/Verify';

const { Paragraph } = Typography;

const NDAList: React.FC = () => {
    const [data, setData] = useState<INDAFormPayload[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [refresh, setRefresh] = useState<boolean>(false);

    useEffect(() => {
        async function LoadData() {
            try {
                UIStore.setSelectedKey(['nda']);
                UIStore.setDocumentTitle('NDA Form');
                await UserStore.getNDAForm().then(data => (setData(data?.results), setLoading(false)));
            } catch (err: any) {
                setLoading(false);
            }
        }

        LoadData();
    }, [refresh]);

    const handleRemove = async (id: number) => {
        try {
            setLoading(true);
            setData(data.filter(item => item.id !== id));

            await UserStore.removeNDAForm(id).then(() => {});
            setLoading(false);
        } catch (err: any) {
            setLoading(false);
            message.error(`Failed removing NDA Form. Error: ${err}`);
        }
    };

    if (!UserStore.isVerified) {
        return <Verify />;
    }

    return (
        <>
            <Alert
                showIcon={true}
                type="info"
                className="mb-3"
                message={
                    <div className="d-flex align-items-center">
                        If you would like to use our MNDA form, please
                        <Button
                            type="link"
                            rel="noopener noreferrer"
                            onClick={() => {
                                const url = '/customer/contacts/download-dna-form/';
                                get(url, { responseType: 'blob' }).then((response: any) => {
                                    DownloadFile(response, 'partsbadger_mdna_form.pdf');
                                });
                            }}
                        >
                            <span className="text-info font-weight-bold p-0 mx-0">Click Here to download</span>
                        </Button>
                    </div>
                }
            />

            <Card title={'NDA Forms'} bordered>
                {/* {this.state.errors && <ErrorRender style={{ padding: '10px' }} error={this.state.errors} />} */}
                <Paragraph>
                    Please submit your NDA form through this form and we will send a signed copy back to you.
                </Paragraph>
                <AddEditNDAForm
                    key="list-add"
                    onChange={(isChange: boolean) => (isChange ? setRefresh(!refresh) : null)}
                />
                <Divider />
                {loading && <Skeleton paragraph={{ rows: 4 }} />}

                <div className="flex flex-wrap">
                    {data.map(item => (
                        <div
                            className="p-0 mr-4 mb-4 rounded w-full"
                            style={{ minWidth: '300px', maxWidth: '410px', textAlign: 'center' }}
                            key={item.id}
                        >
                            <p
                                className="w-full text-center text-xl text-white rounded-t"
                                style={{ backgroundColor: item.signed ? '#87D068' : '#ffb703', margin: 0 }}
                            >
                                {item.signed ? 'Signed' : 'Pending'}
                            </p>
                            <div
                                className="flex flex-col px-5 pt-2"
                                style={{ border: '1px solid #595959', borderTop: 'none', minHeight: '138px' }}
                            >
                                <span className="text-2xl text-black w-full">{item.company_name}</span>
                                {item.files.map(itemFile => {
                                    return (
                                        <a
                                            key={itemFile.id}
                                            href={itemFile.file}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            download="Nda Form"
                                            style={{ color: '#595959', padding: '4px', textDecoration: 'underline' }}
                                            className="flex items-center text-base"
                                        >
                                            <LinkOutlined className="pr-1" style={{ fontSize: 12 }} />
                                            {itemFile.filename.toLowerCase()}
                                        </a>
                                    );
                                })}

                                {item.needs_reupload && (
                                    <Alert
                                        type="warning"
                                        showIcon
                                        className="mt-3 mb-1 text-left"
                                        message="We could not read this file. Please upload a valid PDF NDA."
                                    />
                                )}

                                <div className="flex flex-row w-full justify-between items-center mt-5">
                                    <RemoveConfirm
                                        key="list-remove"
                                        handleRemove={handleRemove}
                                        name={item.name}
                                        record={item}
                                    />
                                    <AddEditNDAForm
                                        key="list-edit"
                                        record={item}
                                        onChange={(isChange: boolean) => (isChange ? setRefresh(!refresh) : null)}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </>
    );
};

export default NDAList;
